/**
 * Bezpečné diagnostické logy pre OAuth / PKCE (žiadne plné tajné kľúče ani celý auth code).
 */

/** Skrátený fingerprint verejného JWT (anon key) — len na porovnanie či .env sedí s očakávaním. */
export function fingerprintPublicJwt(anonKey: string): string {
  const trimmed = anonKey.trim();
  if (trimmed.length === 0) {
    return "(prázdny)";
  }
  if (trimmed.length < 16) {
    return `(príliš krátky, len ${trimmed.length} znakov)`;
  }
  return `${trimmed.slice(0, 12)}…${trimmed.slice(-6)} · dĺžka=${trimmed.length}`;
}

/** Auth code len v skrátenej podobe (debug). */
export function redactOAuthCode(code: string | null): string {
  if (!code) {
    return "(žiadny)";
  }
  if (code.length <= 10) {
    return `(krátky, len ${code.length} znakov)`;
  }
  return `${code.slice(0, 6)}…${code.slice(-4)} · dĺžka=${code.length}`;
}

/** Kľúče v localStorage, ktoré obsahujú podreťazec (napr. sb- alebo code-verifier). */
export function listLocalStorageKeysContaining(substring: string): string[] {
  const keys: string[] = [];
  if (typeof window === "undefined") {
    return keys;
  }
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (key && key.includes(substring)) {
      keys.push(key);
    }
  }
  return keys.sort();
}

export interface OauthPkceVerifierDebug {
  storageKey: string;
  present: boolean;
  valueLength: number;
  /** Kópia z GoTrueClient: `${verifier}/${redirectType}` */
  rawPreview: string | null;
  /** GoTrue ukladá PKCE verifier ako hex (S256 challenge). */
  verifierSegmentLooksLikeGoTrueHex: boolean;
  firstCharCode: number | null;
}

/**
 * Načítanie verejných claimov z JWT (anon/service) — bez overenia podpisu.
 * Pomôže overiť, či kľúč v .env sedí s očakávanou inštanciou (ref, role).
 */
export function decodeSupabaseJwtClaims(
  jwt: string,
): Record<string, unknown> | null {
  const trimmed = jwt.trim();
  if (!trimmed || !trimmed.includes(".")) {
    return null;
  }
  try {
    const payloadPart = trimmed.split(".")[1];
    if (!payloadPart) {
      return null;
    }
    const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function summarizeAnonJwtForDebug(anonKey: string): {
  role: string | undefined;
  ref: string | undefined;
  iss: string | undefined;
  exp: number | undefined;
} | null {
  const claims = decodeSupabaseJwtClaims(anonKey);
  if (!claims) {
    return null;
  }
  return {
    role: typeof claims.role === "string" ? claims.role : undefined,
    ref: typeof claims.ref === "string" ? claims.ref : undefined,
    iss: typeof claims.iss === "string" ? claims.iss : undefined,
    exp: typeof claims.exp === "number" ? claims.exp : undefined,
  };
}

export function inspectPkceVerifier(
  storageKeyBase: string,
): OauthPkceVerifierDebug {
  const fullKey = `${storageKeyBase}-code-verifier`;
  const raw =
    typeof window !== "undefined" ? window.localStorage.getItem(fullKey) : null;
  const valueLength = raw?.length ?? 0;
  const preview =
    raw && valueLength > 0
      ? `${raw.slice(0, 10)}…(${valueLength} znakov)`
      : null;
  const segment = raw && raw.length > 0 ? (raw.split("/")[0] ?? "") : "";
  const verifierSegmentLooksLikeGoTrueHex =
    segment.length > 0 && /^[0-9a-f]+$/i.test(segment);
  const firstCharCode =
    raw && raw.length > 0 ? (raw.codePointAt(0) ?? null) : null;
  return {
    storageKey: fullKey,
    present: Boolean(raw && valueLength > 0),
    valueLength,
    rawPreview: preview,
    verifierSegmentLooksLikeGoTrueHex,
    firstCharCode,
  };
}
